// src/pages/finance-portal/expense-categories/ExpenseCategories.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, IconButton, Alert, Skeleton, Tooltip, Switch,
    Dialog, DialogTitle, DialogContent, DialogActions, Grid,
    Autocomplete, CircularProgress, FormControlLabel,
} from '@mui/material';
import CategoryIcon from '@mui/icons-material/Category';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const defaultCategory = {
    name: '',
    gl_account_id: null,
    max_amount: '',
    requires_receipt: false,
};

export default function ExpenseCategories() {
    const [categories, setCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Form dialog
    const [formOpen, setFormOpen] = useState(false);
    const [editCategory, setEditCategory] = useState(null);
    const [form, setForm] = useState(defaultCategory);
    const [saving, setSaving] = useState(false);

    // Delete dialog
    const [deleteDialog, setDeleteDialog] = useState({ open: false, category: null });
    const [deleting, setDeleting] = useState(false);

    // GL Accounts
    const [glAccounts, setGlAccounts] = useState([]);
    const [glLoading, setGlLoading] = useState(false);

    const fetchCategories = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get('/api/finance/expense-categories');
            if (res.data.success) {
                setCategories(res.data.data || []);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load categories');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { fetchCategories(); }, [fetchCategories]);

    // GL accounts - fetch once on dialog open
    useEffect(() => {
        if (!formOpen) return;
        setGlLoading(true);
        (async () => {
            try {
                const res = await axiosInstance.get('/api/finance/coa', { params: { flat: true } });
                if (res.data.success) setGlAccounts(res.data.data || []);
            } catch (err) {
                console.error('Failed to load GL accounts', err);
            } finally {
                setGlLoading(false);
            }
        })();
    }, [formOpen]);

    const handleOpenAdd = () => {
        setEditCategory(null);
        setForm(defaultCategory);
        setFormOpen(true);
    };

    const handleOpenEdit = (cat) => {
        setEditCategory(cat);
        setForm({
            name: cat.name || '',
            gl_account_id: cat.gl_account_id || null,
            max_amount: cat.max_amount || '',
            requires_receipt: cat.requires_receipt || false,
        });
        setFormOpen(true);
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            let res;
            if (editCategory?.id) {
                res = await axiosInstance.put(`/api/finance/expense-categories/${editCategory.id}`, form);
            } else {
                res = await axiosInstance.post('/api/finance/expense-categories', form);
            }
            if (res.data.success) {
                setFormOpen(false);
                setEditCategory(null);
                setForm(defaultCategory);
                fetchCategories();
            } else {
                setError(res.data.message || 'Failed to save category');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save category');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await axiosInstance.delete(`/api/finance/expense-categories/${deleteDialog.category.id}`);
            setDeleteDialog({ open: false, category: null });
            fetchCategories();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete category');
        } finally {
            setDeleting(false);
        }
    };

    const fieldSx = { fontFamily, fontSize: '0.85rem', borderRadius: '10px' };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CategoryIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                                Expense Categories
                            </Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                                Configure expense categories and policies
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenAdd}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            bgcolor: 'rgba(255,255,255,0.2)', color: '#fff',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                            backdropFilter: 'blur(10px)',
                        }}
                    >
                        Add Category
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {/* Table */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Name', 'GL Account', 'Max Amount', 'Requires Receipt', 'Active', 'Actions'].map(h => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0', letterSpacing: '0.05em' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3, 4, 5].map(i => (
                                        <TableRow key={i}>
                                            <TableCell colSpan={6}><Skeleton height={36} /></TableCell>
                                        </TableRow>
                                    ))
                                ) : categories.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                                            <CategoryIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.95rem' }}>No categories found</Typography>
                                            <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.8rem', mt: 0.5 }}>Add your first expense category</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : categories.map(cat => (
                                    <TableRow key={cat.id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>
                                            {cat.name}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                            {cat.gl_account_name
                                                ? `${cat.gl_account_number || ''} ${cat.gl_account_name}`.trim()
                                                : '-'}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                            {cat.max_amount
                                                ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cat.max_amount)
                                                : '-'}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={cat.requires_receipt ? 'Yes' : 'No'}
                                                size="small"
                                                sx={{
                                                    fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22,
                                                    bgcolor: cat.requires_receipt ? '#dcfce7' : '#f1f5f9',
                                                    color: cat.requires_receipt ? '#166534' : '#64748b',
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={cat.is_active !== false ? 'Active' : 'Inactive'}
                                                size="small"
                                                sx={{
                                                    fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22,
                                                    bgcolor: cat.is_active !== false ? '#dcfce7' : '#f1f5f9',
                                                    color: cat.is_active !== false ? '#166534' : '#64748b',
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <Tooltip title="Edit">
                                                    <IconButton size="small" onClick={() => handleOpenEdit(cat)} sx={{ color: '#94a3b8', '&:hover': { color: '#3b82f6' } }}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setDeleteDialog({ open: true, category: cat })}
                                                        sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            </Box>

            {/* Add/Edit Category Dialog */}
            <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>
                    {editCategory ? 'Edit Category' : 'Add Category'}
                </DialogTitle>
                <DialogContent dividers sx={{ pt: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth size="small" label="Category Name" required
                                value={form.name}
                                onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                                InputProps={{ sx: fieldSx }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Autocomplete
                                size="small"
                                options={glAccounts}
                                getOptionLabel={(opt) => opt ? `${opt.account_number} - ${opt.name}` : ''}
                                value={glAccounts.find(a => a.id === form.gl_account_id) || null}
                                onChange={(_, val) => setForm(prev => ({ ...prev, gl_account_id: val?.id || null }))}
                                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                                loading={glLoading}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="GL Account"
                                        placeholder="Select GL Account..."
                                        InputProps={{
                                            ...params.InputProps,
                                            sx: fieldSx,
                                            endAdornment: (
                                                <>
                                                    {glLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth size="small" label="Max Amount" type="number"
                                value={form.max_amount}
                                onChange={e => setForm(prev => ({ ...prev, max_amount: e.target.value }))}
                                InputProps={{ sx: fieldSx }}
                                helperText="Leave blank for no limit"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={form.requires_receipt}
                                        onChange={e => setForm(prev => ({ ...prev, requires_receipt: e.target.checked }))}
                                        color="success"
                                    />
                                }
                                label={<Typography sx={{ fontFamily, fontSize: '0.85rem' }}>Requires Receipt</Typography>}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setFormOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving || !form.name.trim()}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                    >
                        {saving ? 'Saving...' : editCategory ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, category: null })} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    Delete "{deleteDialog.category?.name}"?
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.9rem' }}>
                        This will permanently delete this expense category. Categories with existing expenses cannot be deleted.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteDialog({ open: false, category: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleDelete}
                        disabled={deleting}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
                    >
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
